import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { SOCKET_URL } from '../config/api';
import { isLikelyInAppWebView, isNativeApp } from '../utils/runtimeEnvironment';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  connectionError: string | null;
}

export const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

function isWebSocketOnlyMode(): boolean {
  if (process.env.REACT_APP_SOCKET_WEBSOCKET_ONLY === 'true') return true;
  return isNativeApp() || isLikelyInAppWebView();
}

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const { token, user } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const websocketOnly = isWebSocketOnlyMode();

  useEffect(() => {
    const cleanupPrevious = () => {
      if (socketRef.current) {
        try {
          socketRef.current.removeAllListeners();
          socketRef.current.disconnect();
        } catch {
          // ignore cleanup errors
        }
        socketRef.current = null;
      }
      setSocket(null);
      setIsConnected(false);
    };

    cleanupPrevious();
    setConnectionError(null);

    const pollingFirst =
      !websocketOnly && process.env.REACT_APP_SOCKET_POLLING_FIRST === 'true';
    const transports = websocketOnly
      ? (['websocket'] as const)
      : pollingFirst
        ? (['polling', 'websocket'] as const)
        : (['websocket', 'polling'] as const);

    const newSocket = io(SOCKET_URL, {
      path: '/socket.io/',
      transports: [...transports],
      upgrade: !websocketOnly,
      withCredentials: true,
      auth: token ? { token } : {},
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      reconnectionAttempts: websocketOnly ? Infinity : 20,
      secure: process.env.NODE_ENV === 'production',
      timeout: 10000,
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    newSocket.on('connect', () => {
      setIsConnected(true);
      setConnectionError(null);
      if (user?.id != null) {
        newSocket.emit('join_user_room', user.id);
      }
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error: Error) => {
      setConnectionError(error?.message || 'تعذر الاتصال بالسيرفر. شغّل السيرفر: npm run dev');
      setIsConnected(false);
      if (
        !websocketOnly &&
        (error?.message?.includes('websocket') || error?.message?.includes('timeout'))
      ) {
        setTimeout(() => {
          try {
            const ioOpts = (newSocket as Socket & { io?: { opts?: { transports?: string[] } } }).io
              ?.opts;
            if (ioOpts) ioOpts.transports = ['polling'];
            newSocket.connect();
          } catch {
            // ignore
          }
        }, 2000);
      }
    });

    newSocket.on('error', (error: Error) => {
      setConnectionError(error?.message || 'خطأ غير معروف');
    });

    newSocket.on('reconnect', () => {
      setIsConnected(true);
      setConnectionError(null);
      if (user?.id != null) newSocket.emit('join_user_room', user.id);
    });

    newSocket.on('reconnect_error', (error: Error) => {
      setConnectionError(error?.message || 'فشل إعادة الاتصال');
    });

    newSocket.on('reconnect_failed', () => {
      if (!websocketOnly) {
        setConnectionError('فشل في إعادة الاتصال. تأكد من تشغيل السيرفر.');
        setIsConnected(false);
      }
    });

    return () => {
      cleanupPrevious();
    };
  }, [token, websocketOnly]);

  useEffect(() => {
    if (socket?.connected && user?.id != null) {
      socket.emit('join_user_room', user.id);
    }
  }, [socket, isConnected, user?.id]);

  return (
    <SocketContext.Provider value={{ socket, isConnected, connectionError }}>
      {children}
    </SocketContext.Provider>
  );
};
